import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type {
  AdminBalance,
  AdminBillingProfile,
  AdminPaymentRow,
} from "../../api/types";
import { errorMessage } from "../../shared/errors";
import {
  formatCredits,
  formatDateTime,
  formatMoney,
} from "../../shared/format";
import { Alert, Badge, Button, DataTable, Input, Modal } from "../../ui";
import { LEDGER_KINDS, ledgerColumns } from "./LedgerPage";
import { Select, StaffAction, stateTone } from "./shared";

/** Balances, billing profile, adjust ledger, payments and ledger for one org — used by the
 * user detail "Orgs & Billing" tab and the org detail page (docs/07 §3). */
export function OrgBilling({
  orgId,
  balances,
  profile,
  payments,
  canMutate,
  onChanged,
}: {
  orgId: string;
  balances: AdminBalance[];
  profile: AdminBillingProfile | null;
  payments?: AdminPaymentRow[];
  canMutate: boolean;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [adjust, setAdjust] = useState<{
    product: string;
    amount: string;
    note: string;
  } | null>(null);
  const [kind, setKind] = useState("");
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const ledger = useQuery({
    queryKey: ["admin", "ledger", { org: orgId, kind }, undefined],
    queryFn: () => adminApi.ledger({ org: orgId, kind, limit: 50 }),
  });
  const submitAdjust = () => {
    if (!adjust) return;
    adminApi
      .adjustLedger(orgId, {
        product: adjust.product,
        amount: Number(adjust.amount),
        note: adjust.note,
      })
      .then((r) => {
        setMessage({
          kind: "success",
          text: `Adjusted. Balance now ${formatCredits(r.balance_after)}.`,
        });
        setAdjust(null);
        onChanged();
        return queryClient.invalidateQueries({ queryKey: ["admin", "ledger"] });
      })
      .catch((err) => {
        setMessage({ kind: "error", text: errorMessage(err) });
        setAdjust(null);
      });
  };
  return (
    <div className="flex flex-col gap-4">
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium">Balances</h3>
          <StaffAction
            allowed={canMutate}
            onClick={() =>
              setAdjust({
                product: balances[0]?.product ?? "",
                amount: "",
                note: "",
              })
            }
          >
            Adjust ledger
          </StaffAction>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-1">Product</th>
              <th className="px-3 py-1">Balance</th>
              <th className="px-3 py-1">Purchased</th>
              <th className="px-3 py-1">Available</th>
              <th className="px-3 py-1">Auto-refill</th>
            </tr>
          </thead>
          <tbody>
            {balances.length === 0 ? (
              <tr>
                <td className="px-3 py-2 text-slate-500" colSpan={5}>
                  No balances yet.
                </td>
              </tr>
            ) : null}
            {balances.map((b) => (
              <tr key={b.product} className="border-t border-slate-100">
                <td className="px-3 py-1">{b.product}</td>
                <td className="px-3 py-1">{formatCredits(b.balance)}</td>
                <td className="px-3 py-1">
                  {formatCredits(b.purchased_balance)}
                </td>
                <td className="px-3 py-1">{formatCredits(b.available)}</td>
                <td className="px-3 py-1">
                  {b.auto_refill?.enabled ? "on" : "off"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-sm">
        <h3 className="font-medium">Billing profile</h3>
        {profile ? (
          <p className="text-slate-600">
            {profile.billing_active ? (
              <Badge tone="success">billing active</Badge>
            ) : (
              <Badge tone="danger">billing suspended</Badge>
            )}{" "}
            Stripe customer {profile.stripe_customer_id ?? "—"} · Pi{" "}
            {profile.pi_username ?? profile.pi_user_uid ?? "—"} · card{" "}
            {profile.default_payment_method
              ? `${profile.default_payment_method.brand ?? ""} ${profile.default_payment_method.last4 ?? ""}`
              : "—"}
            {profile.stripe_customer_id ? (
              <>
                {" · "}
                <a
                  className="text-brand underline"
                  href={`https://dashboard.stripe.com/customers/${profile.stripe_customer_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Stripe portal
                </a>
              </>
            ) : null}
          </p>
        ) : (
          <p className="text-slate-500">No billing profile yet.</p>
        )}
      </div>
      {payments ? (
        <div>
          <h3 className="mb-2 text-sm font-medium">Payments</h3>
          <DataTable<AdminPaymentRow>
            columns={[
              {
                id: "created_at",
                header: "Created",
                cell: ({ row }) => formatDateTime(row.original.created_at),
              },
              { accessorKey: "product", header: "Product" },
              { accessorKey: "provider", header: "Provider" },
              {
                id: "amount",
                header: "Amount",
                cell: ({ row }) =>
                  formatMoney(row.original.amount, row.original.currency),
              },
              {
                id: "state",
                header: "State",
                cell: ({ row }) => (
                  <Badge tone={stateTone(row.original.state)}>
                    {row.original.state}
                  </Badge>
                ),
              },
            ]}
            data={payments}
            emptyMessage="No payments."
          />
          <p className="mt-1 text-xs">
            <Link className="text-brand underline" to="/admin/payments">
              All payments
            </Link>
          </p>
        </div>
      ) : null}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium">Ledger</h3>
          <Select
            label="Kind"
            value={kind}
            onChange={setKind}
            options={[
              { value: "", label: "any" },
              ...LEDGER_KINDS.map((k) => ({ value: k, label: k })),
            ]}
          />
        </div>
        {ledger.data ? (
          <DataTable data={ledger.data.items} columns={ledgerColumns} />
        ) : null}
        <p className="mt-1 text-xs">
          <Link
            className="text-brand underline"
            to={`/admin/ledger?org=${orgId}`}
          >
            Full ledger
          </Link>
        </p>
      </div>
      <Modal
        open={adjust !== null}
        title="Adjust ledger"
        onClose={() => setAdjust(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdjust(null)}>
              Cancel
            </Button>
            <Button
              disabled={!adjust?.product || !adjust?.amount || !adjust?.note}
              onClick={submitAdjust}
            >
              Confirm
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2">
          <Input
            label="Product"
            value={adjust?.product ?? ""}
            onChange={(e) =>
              setAdjust({
                ...(adjust ?? { amount: "", note: "" }),
                product: e.target.value,
              })
            }
          />
          <Input
            label="Amount (signed credits)"
            type="number"
            value={adjust?.amount ?? ""}
            onChange={(e) =>
              setAdjust({
                ...(adjust ?? { product: "", note: "" }),
                amount: e.target.value,
              })
            }
          />
          <Input
            label="Note"
            value={adjust?.note ?? ""}
            onChange={(e) =>
              setAdjust({
                ...(adjust ?? { product: "", amount: "" }),
                note: e.target.value,
              })
            }
          />
        </div>
      </Modal>
    </div>
  );
}
